import React, { useState, useEffect } from "react";
import {
  Card,
  CardContent,
  Typography,
  Button,
  Checkbox,
  IconButton,
} from "@mui/material";
import { Divider } from "@mui/material";
import Alert from "@mui/material/Alert";
import { styled } from "@mui/material/styles";
import { useParams } from "react-router-dom";
import axios from "axios";
import { USER_REFFERAL, PP } from "../../api/paymentCheckout";
import { useMediaQuery } from "@mui/material";

const StyledAlert = styled(Alert)({
  backgroundColor: "#6A58C6",
  fontSize: "8px",
  color: "#fff",
  height: "30px",
  margin: "10px 0 10px 0",
  "& .MuiAlert-icon": {
    color: "#fff",
    fontSize: "16px",
  },
});

const PaymentCard = ({ selectedCode, isCouponApplied, course, refr }) => {
  const params = useParams();
  const isMobile = useMediaQuery("(max-width:600px)");

  const [user, setUser] = useState({});
  const [est, setEst] = useState(0); // Original total before discounts
  const [initialDiscount, setInitialDiscount] = useState(0); // Fixed discount if bundle present
  const [couponDiscount, setCouponDiscount] = useState(0); // Discount from coupon
  const [limit, setLimit] = useState("");
  const [payableTotal, setPayableTotal] = useState(0); // Final payable amount
  const [includeInterviewData, setIncludeInterviewData] = useState(false);

  // Helper function to check if 3 special courses exist
  const areThreeCoursesPresent = (cart) => {
    if (!cart || !Array.isArray(cart)) return false;
    const courseIdsToCheck = [
      "64fb08d372d0fde41877df63",
      "64fb15b672d0fde41877e193",
      "64fb105b72d0fde41877e101",
    ];
    return courseIdsToCheck.every((id) =>
      cart.some((item) => item.courseId === id)
    );
  };

  // Load user and calculate initial discount for bundle courses, and total price
  useEffect(() => {
    axios
      .get(USER_REFFERAL + params.userid)
      .then((response) => {
        const userData = response.data.data;
        setUser(userData);

        if (!userData.cart || userData.cart.length === 0) {
          setEst(0);
          setInitialDiscount(0);
          setCouponDiscount(0);
          setPayableTotal(0);
          return;
        }

        const totalSum = userData.cart.reduce(
          (acc, item) => acc + (item.price || 0),
          0
        );
        setEst(totalSum);

        // ₹100 fixed discount if all three special courses are present
        const fixedDiscount = areThreeCoursesPresent(userData.cart) ? 100 : 0;
        setInitialDiscount(fixedDiscount);

        // Reset coupon discount & payable total on cart update
        setCouponDiscount(0);
        setPayableTotal(totalSum - fixedDiscount);
        setLimit("");
      })
      .catch((err) => {
        console.error("Failed to fetch user data in PaymentCard:", err);
      });
  }, [refr, params.userid]);

  // Calculate coupon discount whenever selectedCode or base total changes
  useEffect(() => {
    if (selectedCode && selectedCode.details) {
      let discount = 0;
      const discountPercent = selectedCode.details.discountpercentage || 0;
      const maxDiscount = selectedCode.details.maximumdiscountvalue || 0;

      discount = (est * discountPercent) / 100;
      if (discount > maxDiscount) {
        discount = maxDiscount;
        setLimit(`Maximum Discount: ₹${maxDiscount}`);
      } else {
        setLimit("");
      }
      setCouponDiscount(Math.round(discount * 100) / 100);
    } else {
      setCouponDiscount(0);
      setLimit("");
    }
  }, [selectedCode, est]);

  // Calculate final payable total considering all discounts and mock test addition
  useEffect(() => {
    let total = est - initialDiscount - couponDiscount;
    if (includeInterviewData) total += 150; // Add mock test cost
    if (total < 0) total = 0; // Prevent negative total

    // Round to 2 decimals
    total = Math.round(total * 100) / 100;

    setPayableTotal(total);
  }, [est, initialDiscount, couponDiscount, includeInterviewData]);

  // Payment initiation handler
  const handlePayNow = () => {
    if (!user.cart || user.cart.length === 0) {
      alert("Your cart is empty.");
      return;
    }
    axios
      .post(PP + "/" + params.id + "/" + params.userid, {
        total: payableTotal,
        courseIds: user.cart.map((cart) => cart.courseId),
      })
      .then((response) => {
        const redirectUrl =
          response.data.data.instrumentResponse.redirectInfo.url;
        window.location.replace(redirectUrl);
      })
      .catch((err) => {
        console.error("Payment initiation error:", err);
      });
  };

  return (
    <Card
      variant="outlined"
      style={{
        width: isMobile ? "100%" : "280px",
        boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
        borderRadius: "16px",
        padding: "16px",
        marginLeft: isMobile ? 0 : "24px",
        height: "auto",
      }}
    >
      <CardContent
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
        }}
      >
        <div style={{ width: "100%" }}>
          {/* Total before discounts */}
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              marginBottom: 4,
            }}
          >
            <Typography variant="subtitle1" color={"grey"}>
              Total
            </Typography>
            <Typography>₹{est.toFixed(2)}</Typography>
          </div>

          {/* Total discount */}
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              marginBottom: 4,
            }}
          >
            <Typography variant="caption">Discount</Typography>
            <Typography variant="caption">
              ₹{(initialDiscount + couponDiscount).toFixed(2)}
            </Typography>
          </div>

          {/* Coupon code applied */}
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              marginBottom: 4,
            }}
          >
            <Typography variant="caption">Coupon Applied</Typography>
            <Typography variant="caption">
              {isCouponApplied && selectedCode ? selectedCode.code : "-"}
            </Typography>
          </div>

          {limit && <StyledAlert severity="info">{limit}</StyledAlert>}

          {/* Mock test addition */}
          {includeInterviewData && (
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                marginBottom: 10,
              }}
            >
              <Typography variant="caption">Mocktest</Typography>
              <Typography variant="caption">₹150</Typography>
            </div>
          )}

          <Divider style={{ marginBottom: 10 }} />

          {/* Select to include interview practice test */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              marginBottom: 10,
            }}
          >
            <Typography variant="body2" style={{ flexGrow: 1 }}>
              Add Interview Practice Mocktest for ₹150/-
            </Typography>
            <Checkbox
              checked={includeInterviewData}
              onChange={() => setIncludeInterviewData((prev) => !prev)}
              color="primary"
            />
          </div>

          <Divider style={{ marginBottom: 10 }} />

          {/* Final payable total */}
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              marginBottom: 10,
            }}
          >
            <Typography variant="h6" color="#6A58C6">
              Total
            </Typography>
            <Typography variant="h6" color="#6A58C6">
              ₹{payableTotal.toFixed(2)}
            </Typography>
          </div>

          {/* Pay Now button */}
          <Button
            variant="contained"
            color="primary"
            fullWidth
            onClick={handlePayNow}
            style={{
              borderRadius: 14,
              backgroundColor: "#6A58C6",
              color: "#fff",
            }}
          >
            PROCEED TO PAY
          </Button>

          <Typography
            variant="caption"
            style={{ marginTop: 10, display: "block", textAlign: "center" }}
          >
            By clicking on Proceed To Pay, you agree to our{" "}
            <a href="/refundpolicy" style={{ color: "blue" }}>
              Refund & Cancellation Policy
            </a>
            .
          </Typography>
        </div>
      </CardContent>
    </Card>
  );
};

export default PaymentCard;
